// ─────────────────────────────────────────────────────────────
// Browser-language one-shot detection
//
// On a visitor's first arrival to the EN root (`/`), peek at
// `navigator.language` / `navigator.languages`. If the browser
// prefers German, send them to the German equivalent of the
// current page (or `/de/` if no translation exists).
//
// Only fires once per visitor — we set a localStorage flag so
// returning users (or anyone who's already chosen a language
// via the footer switcher) are never auto-redirected again.
//
// Server-side (Netlify Edge Function) detection would be more
// robust (no flash of wrong-language content), but this keeps
// the build pure-static and good enough for a marketing site.
// ─────────────────────────────────────────────────────────────

const FLAG_KEY = 'ctrls_locale_chosen'
const SUPPORTED = ['en', 'de'] as const
type Locale = (typeof SUPPORTED)[number]

function browserPrefersGerman(): boolean {
  if (typeof navigator === 'undefined') return false
  const langs: string[] = (navigator.languages as string[] | undefined) ?? [navigator.language]
  for (const raw of langs) {
    const code = (raw || '').toLowerCase().slice(0, 2)
    if (code === 'de') return true
    if (code === 'en') return false // EN ranked higher → don't redirect
  }
  return false
}

function currentLocaleFromPath(pathname: string): Locale {
  if (pathname.startsWith('/de/') || pathname === '/de') return 'de'
  return 'en'
}

/**
 * Map an EN URL to its DE counterpart by prepending `/de`.
 * Page-level translations (e.g. `/produkt/` for DE Product) require
 * the alt-URL map from page context — not available here at the
 * pre-route stage, so we use a structural prefix swap as a best-effort
 * fallback. Editors can override per-page via Strapi slugs later.
 */
function toGermanPath(pathname: string): string {
  if (pathname === '/' || pathname === '') return '/de/'
  return `/de${pathname.startsWith('/') ? pathname : `/${pathname}`}`
}

/**
 * Mark that the visitor has either accepted the auto-detect
 * outcome or made an explicit choice via the language switcher.
 * Should be called from the LanguageSwitcher's onClick.
 */
export function rememberLocaleChoice(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(FLAG_KEY, '1')
    }
  } catch {
    // localStorage can throw in private mode / strict CSP — silently ignore.
  }
}

/**
 * Run once on first visit. Idempotent — no-ops if the flag is set.
 */
export function autoDetectLocale(): void {
  if (typeof window === 'undefined') return
  let alreadyChosen = false
  try {
    alreadyChosen = !!localStorage.getItem(FLAG_KEY)
  } catch {
    // If localStorage isn't available, treat as "already chosen" so we
    // don't redirect every page load.
    alreadyChosen = true
  }
  if (alreadyChosen) return

  const here = window.location.pathname
  const here_locale = currentLocaleFromPath(here)
  // Only redirect when:
  //  - visitor is on an EN page (the default)
  //  - browser prefers German
  // German visitors on /de/* are already where they should be.
  if (here_locale !== 'en') {
    rememberLocaleChoice()
    return
  }
  if (!browserPrefersGerman()) {
    rememberLocaleChoice()
    return
  }
  // We're going to redirect — set the flag now so a redirect loop is
  // impossible if `/de/{path}` doesn't exist (Gatsby 404 page should
  // not auto-detect again).
  rememberLocaleChoice()
  const target = toGermanPath(here) + window.location.search + window.location.hash
  // Replace, not push — the EN URL shouldn't appear in browser history.
  window.location.replace(target)
}
